import { Card } from "@/components/ui/card";
import { DollarSign, TrendingUp, TrendingDown, Percent } from "lucide-react";
import { cn } from "@/lib/utils";

export default function FinancialOverview({ candidate, activities }) {
  // Calculate average price from activities
  const pricesDiscussed = activities
    .filter(a => a.price_discussed)
    .map(a => a.price_discussed);
  
  const averagePrice = pricesDiscussed.length > 0
    ? pricesDiscussed.reduce((sum, price) => sum + price, 0) / pricesDiscussed.length
    : null;

  const latestPrice = pricesDiscussed.length > 0 ? pricesDiscussed[0] : candidate.discussed_price;

  // Calculate price trend
  const priceTrend = pricesDiscussed.length >= 2
    ? ((pricesDiscussed[0] - pricesDiscussed[pricesDiscussed.length - 1]) / pricesDiscussed[pricesDiscussed.length - 1] * 100)
    : null;

  const metrics = [
    {
      label: "Siste Pris Diskutert",
      value: latestPrice ? `${latestPrice.toLocaleString()} kr` : "Ikke satt",
      icon: DollarSign,
      color: "indigo",
    },
    {
      label: "Gjennomsnittlig Pris",
      value: averagePrice ? `${Math.round(averagePrice).toLocaleString()} kr` : "Ikke beregnet",
      icon: TrendingUp,
      color: "blue",
    },
    {
      label: "EBITDA",
      value: candidate.ebitda ? `${candidate.ebitda.toLocaleString()} kr` : "Ikke satt",
      icon: DollarSign,
      color: "emerald",
    },
    {
      label: "Multipel",
      value: candidate.multiple ? `${candidate.multiple}x` : "Ikke beregnet",
      icon: Percent,
      color: "purple",
    },
  ];

  const colorClasses = {
    indigo: "bg-lime-100 text-zinc-900",
    blue: "bg-blue-100 text-blue-600",
    emerald: "bg-emerald-100 text-emerald-600",
    purple: "bg-lime-100 text-zinc-900",
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <Card key={metric.label} className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorClasses[metric.color])}>
                <metric.icon className="w-5 h-5" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-1">{metric.label}</p>
            <p className="text-2xl font-bold text-slate-900">{metric.value}</p>
          </Card>
        ))}
      </div>

      {priceTrend !== null && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3">
            {priceTrend >= 0 ? (
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            ) : (
              <TrendingDown className="w-6 h-6 text-rose-600" />
            )}
            <div>
              <p className="text-sm text-slate-500">Pristrend</p>
              <p className={cn("text-lg font-semibold", priceTrend >= 0 ? "text-emerald-600" : "text-rose-600")}>
                {priceTrend >= 0 ? "+" : ""}{priceTrend.toFixed(1)}%
              </p>
            </div>
          </div>
        </Card>
      )}

      {candidate.revenue && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Selskapsinformasjon</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Omsetning</p>
              <p className="text-xl font-bold text-slate-900">{candidate.revenue.toLocaleString()} kr</p>
            </div>
            {candidate.employees && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Antall Ansatte</p>
                <p className="text-xl font-bold text-slate-900">{candidate.employees}</p>
              </div>
            )}
            {candidate.ebitda && candidate.revenue && (
              <div>
                <p className="text-sm text-slate-500 mb-1">EBITDA Margin</p>
                <p className="text-xl font-bold text-slate-900">
                  {((candidate.ebitda / candidate.revenue) * 100).toFixed(1)}%
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {pricesDiscussed.length > 0 && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Prishistorikk</h3>
          <div className="space-y-3">
            {activities
              .filter(a => a.price_discussed)
              .slice(0, 5)
              .map((activity, index) => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{activity.title}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(activity.date).toLocaleDateString("no-NO")}
                    </p>
                  </div>
                  <p className="font-semibold text-emerald-600">
                    {activity.price_discussed.toLocaleString()} kr
                  </p>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}